import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, Wallet, Plus, Wrench, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/Sheet';
import { VehiclePicker, type VehicleOption } from '@/components/common/VehiclePicker';
import { ProjectFilter } from '@/components/ProjectFilter';
import { useCurrentProject } from '@/contexts/CurrentProjectContext';
import { PageError } from '@/components/PageError';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { RowDeleteButton } from '@/components/common/RowDeleteButton';
import { groupByPartition, fmtShort } from '@/lib/datePartition';
import { DriverSelect, toDriverOption } from '@/components/common/DriverSelect';

interface Advance {
  id: string; code: string; driver_id: string; driver_code: string | null; driver_name: string | null;
  amount_sar: string; purpose: string; purpose_enum: string | null;
  description: string | null; status: string;
  project_id: string | null; project_code: string | null;
  requested_at: string | null; approved_at: string | null; paid_at: string | null;
  recovery_method: string | null; recovered_amount_sar: string | null;
  payment_method: string | null; reference: string | null;
}

interface DriverLite { id: string; code: string | null; fullNameEn: string | null }

const PURPOSES = ['fuel', 'food', 'lodging', 'tolls', 'parking', 'medical', 'family_emergency', 'salary_advance', 'vehicle_repair', 'other'];
const PAYMENT_METHODS = ['cash_on_delivery', 'cash_on_pickup', 'cash_advance', 'bank_transfer', 'cheque', 'credit_15d', 'credit_30d', 'credit_45d', 'credit_60d', 'credit_90d', 'other'];

const STATUS_TONE: Record<string, string> = {
  pending_approval: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  approved:         'bg-sky-500/15 text-sky-700 dark:text-sky-400',
  paid:             'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  recovered:        'bg-muted text-muted-foreground',
  rejected:         'bg-rose-500/15 text-rose-700 dark:text-rose-400',
  written_off:      'bg-muted text-muted-foreground',
};

const fmtSar = (s: string | null) => 'SAR ' + Number(s ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function DriverAdvancesPage() {
  const { t } = useTranslation();
  // PR-R1 — default to the project you're in (NEOM), so advances never show
  // another project's drivers. The ProjectFilter can still switch.
  const { project } = useCurrentProject();
  const [items, setItems] = useState<Advance[]>([]);
  const [drivers, setDrivers] = useState<DriverLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<string | null>(project?.id ?? null);
  useEffect(() => { if (project?.id) setProjectId(project.id); }, [project?.id]);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [creating, setCreating] = useState(false);

  // create form
  const [driverId, setDriverId] = useState('');
  const [amount, setAmount] = useState('');
  const [purpose, setPurpose] = useState<string>('salary_advance');
  const [description, setDescription] = useState('');
  const [paidMethod, setPaidMethod] = useState('cash_advance');
  const [submitting, setSubmitting] = useState(false);

  // PR-NEOM-3 — optional "spent on vehicle maintenance" split. The advance is
  // recovered from the driver's salary by default; whatever is marked here is
  // booked to the truck instead (via the existing advance->maintenance
  // reconciliation) and is NOT deducted from salary. No double counting.
  const [maintAmount, setMaintAmount] = useState('');
  const [maintVehicleId, setMaintVehicleId] = useState<string | null>(null);
  const [maintVehicle, setMaintVehicle] = useState<VehicleOption | null>(null);
  const [maintType, setMaintType] = useState('basic_repair');
  const [resultMsg, setResultMsg] = useState<string | null>(null);

  const amountNum = Number(amount) || 0;
  const maintNum = Number(maintAmount) || 0;
  const salaryNum = Math.max(0, amountNum - maintNum);

  function resetForm() {
    setDriverId(''); setAmount(''); setPurpose('salary_advance'); setDescription('');
    setMaintAmount(''); setMaintVehicleId(null); setMaintVehicle(null); setMaintType('basic_repair');
  }

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (projectId)     params.set('projectId', projectId);
      if (statusFilter)  params.set('status', statusFilter);
      const r = await apiClient.get<Advance[]>('/api/v1/driver-advances-v2?' + params.toString());
      setItems(Array.isArray(r.data) ? r.data : []);
      setError(null);
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setLoading(false); }
  }, [projectId, statusFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    apiClient.get<{ items?: DriverLite[] }>('/api/v1/drivers?pageSize=200&status=active')
      .then((r) => setDrivers(r.data.items ?? []))
      .catch(() => setDrivers([]));
  }, []);

  async function submitCreate() {
    if (!driverId || !amountNum) { window.alert('Driver and amount required'); return; }
    if (maintNum > 0) {
      if (maintNum > amountNum) { window.alert('Maintenance amount cannot exceed the advance amount.'); return; }
      if (!maintVehicleId) { window.alert('Pick the truck the maintenance was for.'); return; }
    }
    // PR-R3 — advances always count immediately (the backend records them paid).
    setSubmitting(true);
    setResultMsg(null);
    try {
      const adv = await apiClient.post<{ id: string; code: string }>('/api/v1/driver-advances-v2', {
        driverId,
        amountSar: amountNum,
        purpose,
        description: description || undefined,
        projectId: projectId || undefined,
        fastTrack: true,
        paidMethod,
      });
      const driverName = drivers.find((d) => d.id === driverId)?.fullNameEn ?? 'driver';
      let msg = `Advance ${adv.data.code}: ${fmtSar(String(salaryNum))} to recover from ${driverName}'s salary`;
      if (maintNum > 0) {
        try {
          await apiClient.post('/api/v1/maintenance/events', {
            vehicleId: maintVehicleId,
            eventType: maintType,
            priority: 'normal',
            paidFromAdvanceId: adv.data.id,
            payerDriverId: driverId,
            expenseAmountSar: maintNum,
            description: `From advance ${adv.data.code}`,
          });
          msg += `; ${fmtSar(String(maintNum))} booked as maintenance on ${maintVehicle?.plateEn ?? 'the truck'} (not deducted).`;
        } catch (e2) {
          msg = `Advance ${adv.data.code} created, but booking the maintenance portion failed: ${extractErrorMessage(e2)}. Add it from the maintenance page.`;
        }
      } else {
        msg += '.';
      }
      setResultMsg(msg);
      setCreating(false);
      resetForm();
      await refresh();
    } catch (e) {
      window.alert(extractErrorMessage(e));
    } finally { setSubmitting(false); }
  }

  // PR-R3 — one-operator: advances are recorded and count immediately. No
  // approve / reject / pay steps.
  const kpiOutstanding = items
    .filter(a => a.status === 'paid' || a.status === 'approved')
    .reduce((sum, a) => sum + Math.max(0, Number(a.amount_sar) - Number(a.recovered_amount_sar ?? 0)), 0);
  const kpiCount = items.length;
  const kpiMonth = items.filter(a => a.paid_at && a.paid_at >= new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString()).length;

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Dashboard</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Wallet className="h-4 w-4" /> {t('advances.title', { defaultValue: 'Driver advances' })}</div>
          <Button size="sm" onClick={() => setCreating(true)} className="gap-1"><Plus className="h-4 w-4" /> New advance</Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-4 space-y-4">
        {resultMsg && (
          <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-400 p-3 text-sm flex items-start gap-2" data-advance-result>
            <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
            <div className="flex-1">{resultMsg}</div>
            <button onClick={() => setResultMsg(null)} className="text-xs underline shrink-0">dismiss</button>
          </div>
        )}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-muted rounded-lg p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Outstanding</div><div className="text-xl font-medium mt-0.5">{fmtSar(String(kpiOutstanding))}</div></div>
          <div className="bg-muted rounded-lg p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Advances</div><div className="text-xl font-medium mt-0.5">{kpiCount}</div></div>
          <div className="bg-muted rounded-lg p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Paid this month</div><div className="text-xl font-medium mt-0.5">{kpiMonth}</div></div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <ProjectFilter value={projectId} onChange={setProjectId} storageKey="advances.projectId" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm">
            <option value="">All statuses</option>
            {Object.keys(STATUS_TONE).map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : error ? (
          <PageError error={error} retry={refresh} />
        ) : items.length === 0 ? (
          <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">No advances yet.</div>
        ) : (
          <div className="rounded-lg border border-border overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="text-xs text-muted-foreground bg-muted/30 border-b border-border">
                <tr>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Code</th>
                  <th className="text-left px-3 py-2">Driver</th>
                  <th className="text-right px-3 py-2">Amount</th>
                  <th className="text-left px-3 py-2">Purpose</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Project</th>
                  <th className="text-right px-3 py-2">Recovered</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* PR-MAY14-DATE-PARTITION — group by Today / Yesterday / This week / etc.
                    Falls back gracefully when an advance has no requested_at yet. */}
                {groupByPartition(items, (a) => a.requested_at ?? a.paid_at).map((g) => (
                  <>
                    <tr key={`hdr-${g.partition}`} className="bg-muted/10">
                      <td colSpan={9} className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                        {g.label} <span className="text-muted-foreground/70 normal-case">({g.items.length})</span>
                      </td>
                    </tr>
                    {g.items.map((a) => (
                      <tr key={a.id} className="border-b border-border last:border-b-0">
                        <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{fmtShort(a.requested_at ?? a.paid_at)}</td>
                        <td className="px-3 py-2 font-mono text-xs">{a.code}</td>
                        <td className="px-3 py-2">{a.driver_name ?? a.driver_code ?? '—'}</td>
                        <td className="px-3 py-2 text-right font-mono">{fmtSar(a.amount_sar)}</td>
                        <td className="px-3 py-2 text-xs">{(a.purpose_enum ?? a.purpose ?? '—').replace(/_/g, ' ')}</td>
                        <td className="px-3 py-2"><span className={'inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ' + (STATUS_TONE[a.status] ?? '')}>{a.status.replace(/_/g, ' ')}</span></td>
                        <td className="px-3 py-2 text-xs font-mono">{a.project_code ?? '—'}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{fmtSar(a.recovered_amount_sar)}</td>
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex gap-1 items-center">
                            <RowDeleteButton
                              endpoint={`/api/v1/driver-advances-v2/${a.id}`}
                              entityKind="advance"
                              label={a.code}
                              onDeleted={refresh}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      <Sheet open={creating} onOpenChange={setCreating}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>New driver advance</SheetTitle>
            <SheetDescription>Request or fast-track an advance.</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto p-6 space-y-3 text-sm">
            <div className="space-y-1">
              <Label>Driver *</Label>
              <DriverSelect
                value={driverId}
                onChange={setDriverId}
                drivers={drivers.map(toDriverOption)}
                noneLabel="Select driver…"
                className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base"
              />
            </div>
            <div className="space-y-1">
              <Label>Amount (SAR) *</Label>
              <Input type="number" step="0.01" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} />
              <div className="flex gap-2 pt-1">
                {[100, 200, 500].map((v) => (
                  <button key={v} type="button" onClick={() => setAmount(String(v))}
                    className="rounded-md border border-input px-3 py-1 text-xs hover:bg-secondary">{v}</button>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <Label>Purpose</Label>
              <select value={purpose} onChange={(e) => setPurpose(e.target.value)}
                className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base">
                {PURPOSES.map((p) => <option key={p} value={p}>{p.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div className="space-y-1"><Label>Description</Label><textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base" /></div>

            {/* PR-NEOM-3 — optional vehicle-maintenance split. */}
            <div className="rounded-md border border-border p-3 space-y-2" data-advance-maint-split>
              <div className="flex items-center gap-2 text-sm font-medium"><Wrench className="h-4 w-4 text-muted-foreground" /> Spent on vehicle maintenance? (optional)</div>
              <p className="text-xs text-muted-foreground">The advance is recovered from the driver's salary by default. Anything you enter here is booked to the truck instead and is not deducted from salary.</p>
              <div className="space-y-1">
                <Label>Amount on maintenance (SAR)</Label>
                <Input type="number" step="0.01" min={0} max={amountNum || undefined} value={maintAmount} onChange={(e) => setMaintAmount(e.target.value)} placeholder="0" />
              </div>
              {maintNum > 0 && (
                <>
                  <div className="space-y-1">
                    <Label>Truck</Label>
                    <VehiclePicker value={maintVehicleId} preferProjectScope="neom"
                      onChange={(id, v) => { setMaintVehicleId(id); setMaintVehicle(v); }} placeholder="Pick the truck…" />
                  </div>
                  <div className="space-y-1">
                    <Label>Maintenance type</Label>
                    <select value={maintType} onChange={(e) => setMaintType(e.target.value)}
                      className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base">
                      {([['basic_repair', 'Basic repair'], ['oil_change', 'Oil change'], ['tire_puncture', 'Tire puncture'], ['tire_replacement', 'Tire change'], ['tire_wear', 'Tire wear'], ['other', 'Other']] as const).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div className="rounded-md bg-muted/40 px-3 py-2 text-xs">
                    From salary: <span className="font-medium">{fmtSar(String(salaryNum))}</span> · To truck: <span className="font-medium">{fmtSar(String(maintNum))}</span>
                  </div>
                </>
              )}
            </div>

            {/* PR-R3 — one-operator: an advance counts immediately, no approval. */}
            <div className="space-y-1">
              <Label>Payment method</Label>
              <select value={paidMethod} onChange={(e) => setPaidMethod(e.target.value)}
                className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base">
                {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
          </div>
          <div className="shrink-0 border-t border-border bg-muted/30 p-6 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setCreating(false)} disabled={submitting}>Cancel</Button>
            <Button onClick={submitCreate} disabled={submitting || !driverId || !amount}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Record advance
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
